import { LogoutOutlined, SettingOutlined, LockOutlined } from '@ant-design/icons';
import { history, useModel, useIntl, FormattedMessage } from '@umijs/max';
import { Spin, Avatar, Space, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { createStyles } from 'antd-style';
import { stringify } from 'querystring';
import React from 'react';
import { flushSync } from 'react-dom';
import { useUser } from '@/contexts/UserContext';
import { ROLES, isSuperAdmin } from '@/services/authService';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const intl = useIntl();
  let user = null;
  try {
    const userContext = useUser();
    user = userContext.user;
  } catch (error) {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      user = JSON.parse(storedUser);
    }
  }

  const getRoleDisplayName = (roleKey: string) => {
    const roleNames: Record<string, string> = {
      [ROLES.SUPER_ADMIN]: intl.formatMessage({ id: 'admin.user.roleSuperAdmin' }),
      [ROLES.ADMIN]: intl.formatMessage({ id: 'admin.user.roleAdmin' }),
      [ROLES.OPERATOR]: intl.formatMessage({ id: 'admin.user.roleOperator' }),
      [ROLES.VIEWER]: intl.formatMessage({ id: 'admin.user.roleViewer' }),
    };
    return roleNames[roleKey] || roleKey;
  };

  if (user) {
    return (
      <Space>
        <span>{user.username}</span>
        <Tag size="small" color="blue">
          {getRoleDisplayName(user.role)}
        </Tag>
      </Space>
    );
  }

  return <span className="anticon">{intl.formatMessage({ id: 'admin.notLoggedIn' })}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({ children }) => {
  const intl = useIntl();
  const { styles } = useStyles();
  const { initialState, setInitialState } = useModel('@@initialState');

  // 尝试获取用户上下文
  let user = null;
  let logout = null;
  try {
    const userContext = useUser();
    user = userContext.user;
    logout = userContext.logout;
  } catch (error) {
    // 如果没有UserProvider，尝试从localStorage获取
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      user = JSON.parse(storedUser);
    }
  }

  /**
   * 退出登录，并且将当前的 url 保存
   */
  const loginOut = async () => {
    try {
      // 如果有用户上下文则使用，否则清除localStorage
      if (logout) {
        logout();
      } else {
        localStorage.removeItem('user');
        localStorage.removeItem('auth_token');
      }
    } catch (error) {
      console.error('Logout error:', error);
    }

    // 清除initialState
    flushSync(() => {
      setInitialState((s) => ({ ...s, currentUser: undefined }));
    });

    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const redirect = urlParams.get('redirect');
    
    if (window.location.pathname !== '/user/login' && !redirect) {
      history.replace({
        pathname: '/user/login',
        search: stringify({
          redirect: pathname + search,
        }),
      });
    }
  };

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      loginOut();
      return;
    }
    if (key === 'settings') {
      history.push('/admin/settings');
      return;
    }
    if (key === 'password') {
      history.push('/account/password');
      return;
    }
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!user) {
    return loading;
  }

  // 根据用户权限动态生成菜单项
  const menuItems = [];

  menuItems.push({
    key: 'password',
    icon: <LockOutlined />,
    label: intl.formatMessage({ id: 'menu.account.changePassword', defaultMessage: '修改密码' }),
  });

  // 如果用户有管理权限，显示系统设置选项
  if (
    isSuperAdmin(user) ||
    user.permissions?.includes('user:create') ||
    user.permissions?.includes('user:update') ||
    user.permissions?.includes('admin:setting')
  ) {
    menuItems.push({
      key: 'settings',
      icon: <SettingOutlined />,
      label: intl.formatMessage({ id: 'menu.account.settings', defaultMessage: '账户设置' }),
    });
  }

  // 添加登出选项
  menuItems.push({
    type: 'divider' as const,
  });
  menuItems.push({
    key: 'logout',
    icon: <LogoutOutlined />,
    label: intl.formatMessage({ id: 'menu.account.logout', defaultMessage: '退出登录' }),
  });

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
